import { BodyStats_Input } from "@/clients/models/BodyStats_Input";
import { PickerValue, ScrollPicker } from "@/components/scroll-picker";
import { QueryKeys } from "@/constants/query-keys";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { OnboardingUnitToggle } from "@/features/onboarding/components/onboarding-unit-toggle";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useUpdateBodyStats } from "@/hooks/requests/body-status/use-update-body-stats";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { triggerHaptic } from "@/utils/haptics";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

type Unit = "metric" | "imperial";

const UNIT_OPTIONS = [
  { value: "metric", label: "Metric" },
  { value: "imperial", label: "Imperial" },
] as const;

const CM_MIN = 120;
const CM_MAX = 220;
const DEFAULT_CM = 172;
const DEFAULT_KG = 70;

const cmToFtIn = (cm: number) => {
  const totalInches = Math.round(cm / 2.54);
  return { ft: Math.floor(totalInches / 12), in: totalInches % 12 };
};

const ftInToCm = (ft: number, inches: number) => Math.round((ft * 12 + inches) * 2.54);

const buildRange = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => String(min + i));

const CM_OPTIONS = buildRange(CM_MIN, CM_MAX);
const FT_OPTIONS = buildRange(3, 7);
const IN_OPTIONS = buildRange(0, 11);

const renderDarkItem = (value: string, selected: boolean, suffix?: string) => (
  <div
    className={cn(
      "flex h-[56px] w-full items-center justify-center text-[22px] font-semibold",
      selected ? "text-white" : "text-[#b1bbd1]",
    )}
  >
    {value}
    {suffix ? <span className="ml-1">{suffix}</span> : null}
  </div>
);

const HeightPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { bodyStats } = useGetBodyStats();
  const queryClient = useQueryClient();
  const { updateBodyStatsAsync, isUpdating } = useUpdateBodyStats();
  const { showError } = useToast();
  const hydratedRef = useRef(false);

  const [unit, setUnit] = useState<Unit>("metric");
  const [cm, setCm] = useState<number>(DEFAULT_CM);

  useEffect(() => {
    if (hydratedRef.current) return;
    const existing = Number(bodyStats?.height);
    if (existing && existing >= CM_MIN && existing <= CM_MAX) {
      setCm(existing);
      hydratedRef.current = true;
    }
  }, [bodyStats?.height]);

  const ftIn = useMemo(() => cmToFtIn(cm), [cm]);
  const metricValue: PickerValue = { height: String(cm) };
  const imperialValue: PickerValue = { ft: String(ftIn.ft), in: String(ftIn.in) };

  const handleSubmit = async () => {
    if (isUpdating) return;
    triggerHaptic();
    const payload: BodyStats_Input = {
      height: cm,
      heightUnit: "cm",
      startingWeight: Number(bodyStats?.startingWeight) || DEFAULT_KG,
      startingWeightUnit: "kg",
      currentWeight: Number(bodyStats?.currentWeight) || DEFAULT_KG,
      currentWeightUnit: "kg",
      targetWeight: Number(bodyStats?.targetWeight) || DEFAULT_KG,
      targetWeightUnit: "kg",
    };
    try {
      await Promise.all([preloadNextRoute(), updateBodyStatsAsync(payload)]);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.BODY_STATS] });
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What’s your height?"
      subtitle="This helps me calculate things more accurately"
      footer={<OnboardingPillButton text="Continue" onClick={handleSubmit} disabled={isUpdating} />}
    >
      <div className="flex flex-col gap-6">
        <OnboardingUnitToggle options={UNIT_OPTIONS} value={unit} onChange={setUnit} />
        {unit === "metric" ? (
          <ScrollPicker
            value={metricValue}
            onChange={(value) => setCm(Number(value.height))}
            height={280}
            itemHeight={56}
            highlightColor="rgba(0,0,0,0.28)"
            style={{ width: "100%" }}
          >
            <ScrollPicker.Column
              name="height"
              options={CM_OPTIONS}
              renderItem={(v, s) => renderDarkItem(v, s, "cm")}
            />
          </ScrollPicker>
        ) : (
          <ScrollPicker
            value={imperialValue}
            onChange={(value) => setCm(ftInToCm(Number(value.ft), Number(value.in)))}
            height={280}
            itemHeight={56}
            highlightColor="rgba(0,0,0,0.28)"
            style={{ width: "100%", gap: "8px" }}
          >
            <ScrollPicker.Column
              name="ft"
              options={FT_OPTIONS}
              renderItem={(v, s) => renderDarkItem(v, s, "ft")}
            />
            <ScrollPicker.Column
              name="in"
              options={IN_OPTIONS}
              renderItem={(v, s) => renderDarkItem(v, s, "in")}
            />
          </ScrollPicker>
        )}
      </div>
    </OnboardingStepShellV5>
  );
};

export default HeightPage;
